import React, { useRef, useState, useReducer, useEffect } from "react";
import { Wizard, WizardPage, IWizardCommonProps } from "cayo.ui.wizards";
import { useIntl } from "react-intl";
import { ChannelName, AccessEntra, SelectUserAccount } from "../shared";
import { communicationChannelsWizardMessages } from "../shared/messages";
import { teamsWizardMessages } from "./messages";
import { ConfigureTeams } from "./steps/ConfigureTeams";
import { Summary } from "./steps/Summary";
import wizardStateReducer, { InitialState } from "./store";
import { logger } from "cayo.ui";
import {
  getConsentUrl,
  exchangeAuthCode,
  promotePermissions,
  getSigninConsentUrl,
  addTeamsConnector,
} from "../shared/api";
import { openAuthWindowAndGetCode, defineChannelName } from "../shared/helpers";
import { GrantStateType, ConfigTeams } from "../shared/types";
import { endpoints } from "@cayo/shared/services/endpoints.service";

type ActionType = (data: {
  credentialType?: string;
  appUrl: string;
  accountName: string;
}) => Promise<any>;

export const Teams = (props: IWizardCommonProps) => {
  const intl = useIntl();
  const wizardRef = useRef<{ resetError: () => void }>(null);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string | undefined>();
  const [wizardState, dispatchWizardState] = useReducer(wizardStateReducer, InitialState);
  const [processing, setProcessing] = useState(false);
  const stateRef = useRef(InitialState);
  stateRef.current = wizardState;

  useEffect(() => {
    defineChannelName(
      "cayo.graph.teamsConnector",
      "Microsoft Teams Communication Channel",
      setLoadingText,
      dispatchWizardState
    );
  }, []);

  const handleChangeField = (form: string, data: any) => {
    dispatchWizardState({
      type: "update",
      payload: {
        form,
        data,
      },
    });
  };

  const handleGrant = (
    accountName: string,
    form: string,
    action: ActionType,
    callback?: (token: string) => void
  ) => {
    setProcessing(true);
    action({
      credentialType: "communicationChannel",
      appUrl: endpoints.redirectUri,
      accountName,
    }).then((res) => {
      if (res?.url) {
        openAuthWindowAndGetCode(res.url)
          .then(({ code, error, error_description }) => {
            if (code) {
              exchangeAuthCode({
                serviceType: "communicationChannel",
                authorizationCode: code,
                accountName,
              }).then((res) => {
                if (res?.value) {
                  setProcessing(false);
                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form,
                      data: {
                        grantState: "Granted",
                        token: res?.value,
                        error: "",
                      },
                    },
                  });

                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form: "grantedAccounts",
                      data: {
                        [form === "adminAccount" ? "admin" : "user"]: accountName,
                      },
                    },
                  });

                  callback && callback(res?.value);
                } else if (res?.message) {
                  setProcessing(false);
                  dispatchWizardState({
                    type: "update",
                    payload: {
                      form,
                      data: {
                        error: res.message,
                      },
                    },
                  });
                }
              });
            } else if (!code && error) {
              setProcessing(false);
              dispatchWizardState({
                type: "update",
                payload: {
                  form,
                  data: {
                    grantState: "Invalid Credentials",
                    error: error_description || "",
                  },
                },
              });
            }
          })
          .catch(() => setProcessing(false));
      } else if (res?.message) {
        setProcessing(false);
        dispatchWizardState({
          type: "update",
          payload: {
            form,
            data: {
              error: res.message,
            },
          },
        });
      }
    });
  };

  return (
    <Wizard
      title={intl.formatMessage(teamsWizardMessages.wizardTitle)}
      isFinished={isFinished}
      activePage={0}
      ref={wizardRef}
      showStepError
      loadingText={loadingText}
      closeConfirmationMessage={" "}
      {...props}
    >
      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.wizardTitle)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.wizardTitle)}
        subTitle=""
        isInputValid={wizardState?.chanelInfo?.name?.trim()}
      >
        <ChannelName
          name={wizardState?.chanelInfo?.name}
          description={wizardState?.chanelInfo?.description}
          handleChange={(key, value) =>
            handleChangeField("chanelInfo", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.accessEntra)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.accessEntra)}
        subTitle=""
        isInputValid={
          wizardState?.adminAccount?.grantState === GrantStateType.granted &&
          wizardState?.adminAccount?.name === wizardState?.grantedAccounts?.admin
        }
      >
        <AccessEntra
          error={wizardState?.adminAccount?.error}
          accountName={wizardState?.adminAccount?.name}
          grantIsActive={!!wizardState?.adminAccount?.name}
          processing={processing}
          accountMissMatch={
            wizardState?.adminAccount?.grantState === GrantStateType.granted &&
            wizardState?.adminAccount?.name !== wizardState?.grantedAccounts?.admin
          }
          onGrant={() =>
            handleGrant(wizardState?.adminAccount?.name, "adminAccount", getConsentUrl, (token) => {
              promotePermissions({
                refreshToken: token,
                accountName: wizardState?.adminAccount?.name,
                credentialType: "communicationChannel",
              }).catch((err) => logger.log(err));
            })
          }
          grantState={wizardState?.adminAccount?.grantState}
          handleChange={(key, value) =>
            handleChangeField("adminAccount", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.selectUserAccount)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.selectUserAccount)}
        subTitle=""
        isInputValid={
          (wizardState?.userAccount?.grantState === GrantStateType.granted &&
            wizardState?.userAccount?.name === wizardState?.grantedAccounts?.user) ||
          !wizardState?.userAccount?.use
        }
      >
        <SelectUserAccount
          error={wizardState?.userAccount?.error}
          accountName={wizardState?.userAccount?.name}
          grantIsActive={!!wizardState?.userAccount?.name}
          processing={processing}
          accountMissMatch={
            wizardState?.userAccount?.use &&
            wizardState?.userAccount?.grantState === GrantStateType.granted &&
            wizardState?.userAccount?.name !== wizardState?.grantedAccounts?.user
          }
          onGrant={() =>
            handleGrant(wizardState?.userAccount?.name, "userAccount", getSigninConsentUrl)
          }
          grantState={wizardState?.userAccount?.grantState}
          handleChange={(key, value) =>
            handleChangeField("userAccount", {
              [key]: value,
            })
          }
          resetError={() => {
            dispatchWizardState({
              type: "update",
              payload: {
                form: "userAccount",
                data: {
                  error: "",
                },
              },
            });
          }}
        />
      </WizardPage>

      <WizardPage
        onNext={() => {
          let data: ConfigTeams = {
            adminAccountName: stateRef.current?.adminAccount?.name,
            adminAccountToken: stateRef.current?.adminAccount?.token,
            team: stateRef.current?.teamsConfig.team,
            channel: stateRef.current?.teamsConfig.channel,
            sendTestMessage: true,
            name: stateRef.current?.chanelInfo?.name,
            description: stateRef.current?.chanelInfo?.description,
            mentionChannel: stateRef.current?.teamsConfig?.mentionChannel,
            mentionTeam: stateRef.current?.teamsConfig?.mentionTeam,
          };

          if (stateRef.current?.userAccount?.use) {
            data = {
              ...data,
              accountName: stateRef.current?.userAccount?.name,
              accountToken: stateRef.current?.userAccount?.token,
            };
          }

          return addTeamsConnector(data, () => setIsFinished(true));
        }}
        title={intl.formatMessage(teamsWizardMessages.configureRecipientsMicrosoftTeams)}
        stepName={intl.formatMessage(teamsWizardMessages.configureMicrosoftTeams)}
        subTitle=""
        isInputValid={wizardState?.teamsConfig.team && wizardState?.teamsConfig.channel}
      >
        <ConfigureTeams
          team={wizardState?.teamsConfig.team}
          channel={wizardState?.teamsConfig.channel}
          mentionTeam={wizardState?.teamsConfig.mentionTeam}
          mentionChannel={wizardState?.teamsConfig.mentionChannel}
          handleChange={(key, value) =>
            handleChangeField("teamsConfig", {
              [key]: value,
            })
          }
        />
      </WizardPage>

      <WizardPage
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(communicationChannelsWizardMessages.summary)}
        stepName={intl.formatMessage(communicationChannelsWizardMessages.summary)}
        subTitle=""
      >
        <Summary
          channelName={wizardState?.chanelInfo?.name}
          chanelDesc={wizardState?.chanelInfo?.description}
          accountName={
            wizardState?.userAccount?.use
              ? wizardState?.userAccount?.name
              : wizardState?.adminAccount?.name
          }
          teamToNotify={wizardState?.teamsConfig.team}
          channelToNotify={wizardState?.teamsConfig.channel}
        />
      </WizardPage>
    </Wizard>
  );
};
